import { noop } from 'lodash';
import { useCallback, useMemo } from 'react';

import { type PublicClip } from '@air/api';
import { type Board, type Clip, type PublicBoard } from '@air/api/types';

import { useBoardsBeingDeleted } from '~/swr-hooks/boards/useBoardsBeingDeleted';
import { getAssetFromAssetsData, getBoardsFromBoardsData } from '~/swr-hooks/gallery/galleryView/utils';
import { type UseGalleryViewItemsParams, type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';

export const useGalleryViewItems = <B extends PublicBoard | Board, C extends Clip | PublicClip>({
  boards,
  clips,
  files,
}: UseGalleryViewItemsParams<B, C>): UseGalleryViewItemsReturn<B, C> => {
  const boardsBeingDeleted = useBoardsBeingDeleted();
  const loadNextPage = useCallback(() => {
    if (boards.isValidating || clips.isValidating || files.isValidating) {
      return;
    }
    if (boards.data && boards.hasMore) {
      boards.loadNextPage();
    } else if (clips.data && clips.hasMore) {
      clips.loadNextPage();
    } else if (files.data && files.hasMore) {
      files.loadNextPage();
    }
  }, [boards, clips, files]);

  const isLoadingInitialData =
    !boards.data?.[0]?.data?.length &&
    !clips.data?.[0]?.data?.clips?.length &&
    !files.data?.[0]?.data?.clips?.length &&
    (boards.isValidating || clips.isValidating || files.isValidating);

  const clipsItems = getAssetFromAssetsData(clips.data);
  const filesItems = getAssetFromAssetsData(files.data);
  const boardItems = getBoardsFromBoardsData(boards.data, boardsBeingDeleted);

  const boardsBeingDeletedCount = useMemo(() => {
    if (boardsBeingDeleted.length === 0) {
      return 0;
    }

    return (
      boards.data
        ?.flatMap((page) => page.data)
        .reduce((acc, board) => {
          return acc + (boardsBeingDeleted.includes(board.id) ? 1 : 0);
        }, 0) ?? 0
    );
  }, [boards.data, boardsBeingDeleted]);
  // An empty page list is not "no query": callers pass `[]` when the boards request is skipped.
  // Indexing that list yields undefined, and `undefined - 0` is NaN, which is not an empty total.
  const boardCount =
    (boards.data?.length ? (boards.data[boards.data.length - 1]?.total ?? 0) : 0) - boardsBeingDeletedCount;

  return {
    data: {
      boards: {
        items: boardItems,
        hasMore: boards.hasMore,
        total: boardCount,
        isLoading: boards.isVisible && boards.isValidating,
      },
      clips: {
        items: clipsItems,
        total: clips.data?.length ? (clips.data[clips.data.length - 1]?.data?.total ?? 0) : 0,
        hasMore: clips.hasMore,
        isLoading: clips.isVisible && clips.isValidating,
      },
      files: {
        items: filesItems,
        total: files.data?.length ? (files.data[files.data.length - 1]?.data?.total ?? 0) : 0,
        hasMore: files.hasMore,
        isLoading: files.isVisible && files.isValidating,
      },
    },
    loadNextPage: boards.hasMore || clips.hasMore || files.hasMore ? loadNextPage : noop,
    isLoading: isLoadingInitialData,
    /*
     * The boards half is measured against `boardItems`, not the raw pages, so that a board whose
     * deletion is still running can't hold the empty state back. Its card is already out of the grid
     * and its `total` above already discounts it, while the cached pages only drop it once the async
     * delete task completes — so reading them here left a board whose last sub-board was deleted
     * showing an empty grid with no empty state in it until that finished.
     */
    isEmpty:
      !boards.isValidating &&
      boardItems.length === 0 &&
      !clips.isValidating &&
      !clips.data?.some(({ data: { clips } }) => clips.length > 0) &&
      !files.isValidating &&
      !files.data?.some(({ data: { clips } }) => clips.length > 0),
  };
};
